// Shared body-scroll lock with refcount.
//
// Multiple components in the portal want to prevent the page from scrolling
// while they're open (Modal, MobileBoxSelectionButton, CuratedBoxPreviewButton,
// LoadingOverlay, …). Before this util each one toggled `overflow-hidden` on
// `<body>` independently, which led to two real bugs:
//
//   1. Whichever component closed last wiped the class even if another locker
//      was still open → page became scrollable behind a still-visible modal.
//   2. Some lockers only touched <body>, others touched both <body> and <html>,
//      so the two ended up in different states. On iOS Safari this presents
//      as "page can't scroll" because root-element overflow controls viewport
//      scroll there.
//
// Using a single module-level refcount and always touching both elements
// removes both problems. Callers should pair every `acquireScrollLock()` with
// exactly one `releaseScrollLock()` (typically in a useEffect cleanup).

let _lockCount = 0;

const applyLock = () => {
  document.body.classList.add('overflow-hidden');
  document.documentElement.classList.add('overflow-hidden');
};

const removeLock = () => {
  document.body.classList.remove('overflow-hidden');
  document.documentElement.classList.remove('overflow-hidden');
};

export const acquireScrollLock = () => {
  _lockCount += 1;
  if (_lockCount === 1) applyLock();
};

export const releaseScrollLock = () => {
  _lockCount = Math.max(0, _lockCount - 1);
  if (_lockCount === 0) removeLock();
};

// Panic button. Forces the lock state back to "no lockers held". Intended for
// global recovery hooks (route change, visibility change, error boundary)
// where we'd rather lose a real lock than strand the user with a frozen page.
export const resetScrollLock = () => {
  _lockCount = 0;
  removeLock();
};

// Test/diagnostic only
export const _debugGetLockCount = () => _lockCount;
